import {ChangeDetectionStrategy, Component, Inject, OnInit, PLATFORM_ID, ViewChild} from '@angular/core';
import {NgxModal} from '@ngx-component-kit/cards/modal/modal.component';
import {NgxTranslations} from '@ngx-translations/translations.service';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ImageService} from '@core/services/image.service';
import {AuthService} from '@core/services/auth.service';
import {isPlatformBrowser} from '@angular/common';
import {Constants} from '@app/app.constants';
import {BehaviorSubject} from 'rxjs';
import {map} from 'rxjs/operators';

@Component({
    selector: 'app-change-profile-image',
    templateUrl: './change-profile-image.component.html',
    styleUrls: ['./change-profile-image.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangeProfileImageComponent implements OnInit {

    // Modal.
    @ViewChild('modal', { static: true }) modal: NgxModal;

    // Form.
    form: FormGroup;

    // State.
    isLoading$ = new BehaviorSubject(false);
    image$ = new BehaviorSubject(Constants.assets.empty);

    constructor(private _auth: AuthService,
                private _image: ImageService,
                private _translate: NgxTranslations,
                @Inject(PLATFORM_ID) private _platformId: object) {
    }

    get avatar() {
        return this.form.get('avatar');
    }

    ngOnInit() {
        this.form = new FormGroup({
            avatar: new FormControl(null, Validators.required)
        });
    }

    async onSubmit() {
        // Create a file object from the DataURI.
        const file = this._image.toFile(this.image$.getValue());

        // Set loading.
        this.isLoading$.next(true);

        // Save the avatar via the API.
        await this._auth.setAvatar(file);

        // Close the modal.
        this.modal.quit();
    }

    async setDefaultImage() {
        if (!isPlatformBrowser(this._platformId)) {
            return null;
        }

        // Set the default image.
        const image = new Image();
        image.onload = async () => {
            const dataUri = this._image.toDataUri(image, 250, 250);
            this.image$.next(dataUri);

            // Run the default submit.
            return this.onSubmit();
        };

        image.src = 'assets/images/user-placeholder.png';
    }

    get isValid() {
        return this.image$.pipe(
            map(image => Constants.assets.empty !== image)
        );
    }

    onFileChanged(event) {
        if (!isPlatformBrowser(this._platformId)) {
            return null;
        }

        // Retrieve the file from the event.
        const file = event.target.files[0];

        // Check if the file exists.
        if (!file) {
            this.reset();
            return;
        }

        // Check if the file really is an image.
        if (!file.name.match(/.(jpg|jpeg|png)$/i)) {
            if (window) {
                alert(this._translate.instant('The image does not meet the required format.'));
            }
            this.reset();
            return;
        }

        // Load the file in the memory and convert it to a DataURI.
        const image = new Image();
        const fileReader = new FileReader();
        fileReader.onloadend = (data: any) => {
            image.onload = () => {
                const dataUri = this._image.toDataUri(image, 250, 250);
                this.image$.next(dataUri);
            };

            image.src = data.target.result;
        };

        fileReader.readAsDataURL(file);
    }

    private reset() {
        this.avatar.reset();
        this.image$.next(Constants.assets.empty);
    }

}
